import fetch from 'isomorphic-fetch';
import { parseDate } from '../common/calendar';

export const MAX_MINUTE_DAY = 60 * 24;

export const zeroPadd = num => `0${num}`.slice(-2);

export const minToHour = (min, sep) => {
    const hour = Math.floor(min / 60);
    const minutes = min % 60;
    sep = sep ?? ':';

    return `${zeroPadd(hour)}${sep}${zeroPadd(minutes)}`;
};

export const getMetaContent = key => {
    const els = document.getElementsByTagName('meta');
    const el = [...els].find(el => el.name === key);

    return el ? el.content : null;
};

export const csrfToken = () => getMetaContent('csrf-token');

export const planneParam = key => window.PLANNE_PARAMS[key];

export const buildQueryString = (obj) => {
    const str = [];

    Object.keys(obj).forEach((p) => {
        if (obj[p]) {
          str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
        }
    });

    return str.join('&');
};

export function inIframe () {
    return window !== window.parent;
}

export function getQueryParam(param, defaultValue = undefined) {
    const urlParams = new URLSearchParams(window.location.search);
    const myParam = urlParams.get(param);

    return myParam ?? defaultValue;
}

const jsonMimeType = 'application/json';

export const requestJson = (url, params = null, method = null, external = false, signal = null) => {
    if(!method){
        method = 'get';
    }

    if(typeof method === 'string'){
        method = method.toUpperCase();
    }

    const init = {
        credentials: 'same-origin',
        headers: new Headers({
            'Accept': jsonMimeType
        })
    };

    if (signal) {
        init.signal = signal;
    }

    if(method !== 'GET'){
        init.method = method;
        init.headers.append('X-CSRF-TOKEN', decodeURIComponent(csrfToken()));
    }

    let queryParams = {};
    if (!external) {
        queryParams.token = window.accessToken;
        queryParams.nd = getQueryParam('nd');
    }

    if(params){
        if(method === 'GET' && Object.keys(params).length){
            queryParams = { ...queryParams, ...params };
        }

        if(method !== 'GET'){
            if(params instanceof FormData){
                init.body = params;
            }
            else{
                init.headers.append('Content-Type', jsonMimeType);
                init.body = JSON.stringify(params);
            }
        }
    }

    if (Object.keys(queryParams).length) {
        const queryString = buildQueryString(queryParams);
        url = `${url}?${queryString}`;
    }

    return fetch(url, init)
        .then(response => {
            if ([401, 419].includes(response.status)) {
                window.location.reload();
            }

            return response.json();
        });
};

export const requestAllPagesJson = async (url, params = null) => {
    params = params ?? {};
    const size = 20;

    let arr = [];
    for (let hasMorePages = true, page = 1; hasMorePages; page++) {
        const items = await requestJson(url, { ...params, page, size });
        hasMorePages = items.length >= size;
        arr = [ ...arr, ...items ];
    }

    return arr;
};

export const requestAllPagesJsonWithCallback = async (url, params = null, callback) => {
    params = params ?? {};
    const size = 20;

    let arr = [];
    for (let hasMorePages = true, page = 1; hasMorePages; page++) {
        const items = await requestJson(url, { ...params, page, size });
        hasMorePages = items.length >= size;
        arr = [ ...arr, ...items ];

        callback([...arr], !hasMorePages);
    }
};

export const groupArray = (arr, num) => {
    return arr.reduce((carry, item) => {
        const curr = carry.length && carry[carry.length - 1] ? carry[carry.length - 1] : null;
        if(curr && curr.length < num)
            curr.push(item);
        else
            carry.push([item]);

        return carry;
    }, []);
};

const strToMillis = str => str ? +parseDate(str) : null;

export const formatCents = (num, symbol = true) => {
    const value = (num / 100).toFixed(2).replace(/\.(\d{2})$/, (_, m) => `,${m}`);
    return (symbol ? 'R$ ' : '') + value;
};

export const validation = {
    productScheduleContainsDate(schedulings, dt){
        return {
            ok: schedulings.some(sch => sch.date === dt.format('YYYY-MM-DD')),
            message: 'Não há agendamentos desse produto para a data selecionada',
        };
    },
    productScheduleContainsDateTime(schedulings, dt, minutes){
        const filtered = schedulings.filter(sch => sch.date === dt.format('YYYY-MM-DD'));

        return {
            ok: filtered.some(sch => sch.availableScheduleTimes.some(m => m.minutes == minutes)),
            message: 'Não há agendamentos desse produto para o horário selecionado',
        };
    },
    productScheduleDateTimeIsAvailable(schedulings, dt, minutes, amount){
        const message = 'A data/hora selecionada não tem disponibilidade suficiente para a reserva';
        const dateSchedulings = schedulings.filter(sch => sch.date === dt.format('YYYY-MM-DD'));

        if (!dateSchedulings.length) {
            return { ok: false, message };
        }

        let sq = null;
        for (const sch of dateSchedulings) {
            const scheduleQuantities = sch.occupationRates.scheduleQuantities;
            sq = minutes === null
                ? scheduleQuantities[0]
                : scheduleQuantities.find(sq => sq.minutes == minutes);

            if (sq) {
                break;
            }
        }

        return {
            // Once the tariff occupation is available, the general occupation can be null or 0
            ok: !!(sq && (sq.occupation.availableAmount === null || sq.occupation.availableAmount === 0 || sq.occupation.availableAmount >= amount)),
            message,
        };
    },
    tariffScheduleDateTimeIsAvailable(schedulings, tariff, dt, minutes, amount){
        const message = `A data/hora selecionada não tem disponibilidade suficiente para a tarifa ${tariff.type.name}`;

        const schDate = schedulings.find(sch => sch.date === dt.format('YYYY-MM-DD') && (minutes === null || sch.availableScheduleTimes.some(st => st.minutes == minutes)));
        if (schDate == null) {
            return { ok: false, message };
        }

        const tariffQuantities = schDate.occupationRates.tariffScheduleQuantities.find(tsq => tsq.tariffId == `${tariff.id}`);
        if (tariffQuantities == null) {
            return { ok: false, message };
        }

        const sq = minutes === null ? tariffQuantities.occupationSchedule[0] : tariffQuantities.occupationSchedule.find(sq => sq.minutes == minutes);

        return {
            ok: !!(sq && (sq.occupation.availableAmount === null || sq.occupation.availableAmount >= amount)),
            message,
        };
    },
    resourceIsAvailable(seatMap, res, currentResourceIds){
        const message = `O lugar ${res.name} já está ocupado por uma reserva`;

        for (let area of seatMap.areas) {
            for (let slot of area.slots) {
                if (slot.type !== 'resource' || slot.resource.id !== res.id) {
                    continue;
                }

                const taken = slot.resource.taken && !currentResourceIds.includes(slot.resource.id);

                return {
                    ok: !taken,
                    message: message + (slot.resource.salePosition ? ` da compra #${slot.resource.salePosition}` : ''),
                };
            }
        }

        return { ok: true, message };
    },
    selectedResourcesCapacityMatchTariffQuantities(selectedResources, tariffSum){
        const selectedCapacitySum = selectedResources.reduce((sum, res) => sum + res.capacity ?? 0, 0);

        return {
            ok: tariffSum === selectedCapacitySum,
            message: 'O(s) lugar(es) selecionado(s) comporta(m) um número diferente da quantidade de tarifas selecionadas.',
        };
    },
    selectedResourcesPricingMatchOriginalPrice(seatMap, selectedResources, tariffsCents, originalCents){
        const categoryIndex = seatMap.categories.reduce((idx, cat) => ({ ...idx, [cat.id]: cat }), {});
        const valueSum = selectedResources.reduce((sum, res) => {
            const cat = categoryIndex[res.categoryId];
            return sum + (cat.complete_capacity_charge_type === 'fixed_value' ? cat.min_capacity_unit_cents * res.capacity : 0);
        }, 0);

        const extraPriceCents = Math.max(valueSum - tariffsCents, 0);

        return {
            ok: extraPriceCents === originalCents,
            message: 'O(s) lugar(es) selecionado(s) possuem um valor minimo diferente do valor da reserva original.',
        };
    },
};

/**
 * Conflicts the operator confirmed, as the flags that tell the core to let each one through. A check
 * that passed lifts nothing: only what was actually shown and accepted is forwarded.
 */
export const confirmedVerifications = errors => errors.reduce((acc, { ok, verifications }) => {
    if (ok || !verifications) {
        return acc;
    }

    return verifications.reduce((carry, name) => ({ ...carry, [name]: true }), acc);
}, {});

export const confirmValidationErrors = errors => {
    const messages = errors.filter(({ ok }) => !ok).map(({ message }) => message);
    if (messages.length === 0) {
        return true;
    }

    const str = ['A reserva que está sendo salva apresenta os seguintes conflitos:', '', ...messages].join("\n");
    return confirm(str);
};

export const parseJwt = (token) => {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}
